import { fg } from '@atlaskit/platform-feature-flags/fg';
import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

// --- Types ---

/** User-level token metadata for the shared Twilio client. */
export type TwilioClientTokenMeta = {
	token: string;
	/** Unix seconds when token expires */
	expiration: number;
	identity: string;
};

// --- Client token localStorage ---

const TWILIO_CLIENT_TOKEN_KEY = '__TWILIO_CLIENT_TOKEN__';

export const getTwilioClientToken = (): TwilioClientTokenMeta | null => {
	try {
		const token = fg('csm-cm04-use-atlassian-browser-storage-controls')
			? AtlBrowserStorageLocal.getItem(TWILIO_CLIENT_TOKEN_KEY)
			: // eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
				localStorage.getItem(TWILIO_CLIENT_TOKEN_KEY);
		return token ? JSON.parse(token) : null;
	} catch (error) {
		logException({
			exception: error,
			name: 'TwilioClientTokenParseFailed',
		});
		return null;
	}
};

export const setTwilioClientToken = (tokenMeta: TwilioClientTokenMeta): void => {
	try {
		if (fg('csm-cm04-use-atlassian-browser-storage-controls')) {
			// `setItem` never throws and write failures are already logged internally — see https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/2cfe787/platform/packages/policy-platform/browser-storage-controls/src/services/web-storage-service/utils/set-storage-item.ts#lines-61:65
			AtlBrowserStorageLocal.setItem(TWILIO_CLIENT_TOKEN_KEY, JSON.stringify(tokenMeta));
		} else {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			localStorage.setItem(TWILIO_CLIENT_TOKEN_KEY, JSON.stringify(tokenMeta));
		}
	} catch (error) {
		logException({
			exception: error,
			name: 'TwilioClientTokenSaveFailed',
		});
	}
};

export const removeTwilioClientToken = (): void => {
	try {
		if (fg('csm-cm04-use-atlassian-browser-storage-controls')) {
			AtlBrowserStorageLocal.removeItem(TWILIO_CLIENT_TOKEN_KEY);
		} else {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			localStorage.removeItem(TWILIO_CLIENT_TOKEN_KEY);
		}
	} catch (error) {
		logException({
			exception: error,
			name: 'TwilioClientTokenRemoveFailed',
		});
	}
};
